import { cs } from '../../../lib/currency';
/**
 * iter334cg — Tenant admin's view of their SaaS plan + billing history.
 *
 * Rendered under Business Settings → Plan & Billing tab. Reads from
 * `GET /api/admin/business-settings/plan-and-billing` which returns
 * the tenant's plan id, effective price, discount config, and up to
 * 24 months of prior SaaS invoices from the platform's `db`.
 *
 * All data is READ-ONLY here — plan changes must go through the
 * Super Admin panel. The tenant admin only sees the current state
 * plus history.
 */
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { AlertCircle, CheckCircle2, CreditCard, Loader2, Receipt } from 'lucide-react';
import api from '../../../services/api';
import SaasCardManager from '../../../components/billing/SaasCardManager';
import { PlanChangeCard } from './PlanChangeCard';

export function PlanAndBillingTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    return api.get('/admin/business-settings/plan-and-billing')
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-8" data-testid="plan-billing-loading">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading plan &amp; billing…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm text-red-600" data-testid="plan-billing-error">
        Could not load plan &amp; billing: {error}
      </div>
    );
  }
  if (!data || !data.plan_id) {
    return (
      <div className="space-y-6" data-testid="plan-billing-tab">
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600"
             data-testid="plan-billing-not-configured">
          No SaaS billing profile has been set up for your account yet.
          Pick a plan below to get started — your first bill is generated on the 1st of next month.
        </div>
        <PlanChangeCard onChanged={load} />
      </div>
    );
  }

  const status = String(data.status || 'active').toLowerCase();
  const statusBadge = status === 'active'
    ? { icon: CheckCircle2, label: 'Active', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
    : status === 'past_due'
      ? { icon: AlertCircle, label: 'Past Due', cls: 'bg-amber-50 text-amber-800 border-amber-200' }
      : status === 'suspended'
        ? { icon: AlertCircle, label: 'Suspended', cls: 'bg-red-50 text-red-700 border-red-200' }
        : { icon: CheckCircle2, label: status, cls: 'bg-slate-100 text-slate-700 border-slate-200' };
  const StatusIcon = statusBadge.icon;

  const disc = data.discount;
  const hasDiscount = disc && disc.kind && disc.kind !== 'none' && Number(disc.amount) > 0;

  return (
    <div className="space-y-6" data-testid="plan-billing-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              Current Plan
            </span>
            <Badge variant="outline" className={statusBadge.cls} data-testid="plan-status-badge">
              <StatusIcon className="h-3 w-3 mr-1 inline-block" />
              {statusBadge.label}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Plan</p>
              <p className="font-semibold text-slate-900" data-testid="plan-id">{data.plan_id}</p>
            </div>
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Monthly Price</p>
              <p className="font-semibold text-slate-900" data-testid="plan-monthly-price">
                {cs()}{Number(data.monthly_price || 0).toFixed(2)}
                {hasDiscount && (
                  <span className="ml-2 text-xs font-normal text-emerald-700">
                    ({disc.kind === 'percent'
                      ? `${disc.amount}% off`
                      : `${cs()}${Number(disc.amount).toFixed(2)} off`}
                    {disc.applies_until ? ` until ${disc.applies_until.slice(0, 10)}` : ''})
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Payment Method</p>
              <p className="font-semibold text-slate-900" data-testid="plan-payment-method">
                {data.has_payment_method ? 'Card on file' : 'No card on file'}
              </p>
            </div>
            <div>
              <p className="text-slate-500 text-xs uppercase tracking-wide">Next Bill Date</p>
              <p className="font-semibold text-slate-900" data-testid="plan-next-bill">
                {data.next_bill_date || 'On the 1st of next month'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Manage cards on file — tenant self-service (cards only, one primary) */}
      <SaasCardManager basePath="/admin/business-settings" title="Cards on File" />

      {/* iter335e — Self-service plan change (upgrade/downgrade + add-ons) */}
      <PlanChangeCard onChanged={load} />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-slate-600" />
            Billing History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(!data.invoices || data.invoices.length === 0) ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="plan-invoices-empty">
              No SaaS invoices yet. Your first bill will be generated on the 1st of next month.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="plan-invoices-table">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 px-3 text-left">Period</th>
                    <th className="py-2 px-3 text-right">Amount</th>
                    <th className="py-2 px-3 text-center">Status</th>
                    <th className="py-2 px-3 text-left">Charged</th>
                  </tr>
                </thead>
                <tbody>
                  {data.invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-100" data-testid={`plan-invoice-row-${inv.id}`}>
                      <td className="py-2 px-3">{inv.period_label || inv.period_start}</td>
                      <td className="py-2 px-3 text-right tabular-nums">
                        {cs()}{Number(inv.amount || 0).toFixed(2)}
                        {inv.base_amount && inv.base_amount !== inv.amount && (
                          <span className="block text-[10px] text-slate-400 line-through">
                            {cs()}{Number(inv.base_amount).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <Badge variant="outline" className={
                          inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          inv.status === 'past_due' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                          'bg-slate-100 text-slate-700 border-slate-200'
                        }>
                          {inv.status}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-slate-600 text-xs">
                        {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
